import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import { Modal as BaseModal, Wizard } from '@ubeya/shared-web/components';
import useGenericModules from '@ubeya/shared/hooks/generic/useGenericModules';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import { media } from '@ubeya/shared-web/style';
import YourCompany from './YourCompany';
import DefinePositions from './DefinePositions';
// import DefineModules from './DefineModules';
import SettingAccountLoader from './SettingAccountLoader';
import InitialCompanyDetails from './InitialCompanyDetails';

const Modal = styled(BaseModal)`
  ${media.mobile`
    max-width: 740px;
    max-height: 770px;
    height: 95vh;
  `}

  > .content {
    > form {
      overflow: hidden;

      > div:first-child {
        padding: 55px 0 33px;
        width: 90%;
        margin: 0 auto;
        ${media.mobile`width:auto; min-width: 470px;`}
      }
    }
  }
`;

//all modules but Directory, Compliance and Hiring should be on as default
const TURN_OFF_MODULES = ['directory', 'compliance', 'ats'];

const NewAccountOnboarding = () => {
  const { addAccount } = useAccount();
  const { modulesOptions } = useGenericModules();
  const [showSubmittingLoader, setShowSubmittingLoader] = useState(false);
  const { search: query = '' } = useLocation();
  const parsedQuery = qs.parse(query.slice(1));
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.setQueryData(['modules', undefined], { data: modulesOptions });
  }, [modulesOptions, queryClient]);

  const initialValues = useMemo(
    () => ({
      days: 1,
      modules: modulesOptions
        .filter(({ slug }) => !TURN_OFF_MODULES.includes(slug))
        .reduce((prev, { value }) => ({ ...prev, [`field-${value.toString()}`]: true }), {})
    }),
    [modulesOptions]
  );

  const handleSubmit = useCallback(
    async (values) => {
      setShowSubmittingLoader(true);

      const data = await addAccount({
        name: parsedQuery?.name || values.name,
        industryId: values.industryId,
        countryId: parsedQuery?.countryId || values.countryId,
        size: values.size,
        role: values.role,
        members: values.members,
        memberTypes: values.memberTypes,
        positions: values.positions,
        firstDayOfWeek: 2, // always monday currently
        modules: Object.entries(values.modules || {})
          .filter(([, value]) => !!value)
          .map(([key]) => parseInt(key.replace('field-', ''), 10))
      });

      setTimeout(() => {
        window.location = `${process.env.WEB_URL}/${data.data.id}`;
      }, 1500);
    },
    [addAccount, parsedQuery]
  );

  const hasInitialDetails = !!parsedQuery?.name;

  return (
    <Modal showConfirmButton={false} closeOnClickOutside={false} isPaddingContent={false}>
      <Wizard initialValues={initialValues} onSubmit={handleSubmit}>
        {!hasInitialDetails && (
          <Wizard.Page validate={({ name, countryId }) => (name && countryId ? undefined : { error: true })}>
            <InitialCompanyDetails />
          </Wizard.Page>
        )}
        <Wizard.Page
          validate={({ industryId, size, role }) => (industryId && size && role ? undefined : { error: true })}>
          <YourCompany showTitle={hasInitialDetails} />
        </Wizard.Page>

        <Wizard.Page>
          <DefinePositions />
        </Wizard.Page>

        {/* <Wizard.Page>
          <DefineModules />
        </Wizard.Page> */}
      </Wizard>

      {showSubmittingLoader && <SettingAccountLoader />}
    </Modal>
  );
};

export default NewAccountOnboarding;
